/**
 * Keyboard focus after the place changed without the user moving it there (URL routes, D6 extended): back /
 * forward, a room or view picked in the navigation drawer, the link launcher closed, or a panel or drawer closed
 * with the focus inside it. The view's heading (its `h1`) takes the focus, so keyboard and screen-reader users
 * continue at the new place instead of at the top of the page. Pure DOM; `ViewFocus` drives it from the app state.
 */

/** Marks a heading that took the focus this way (styled without a focus ring: it is not a control). */
export const VIEW_HEADING_ATTR = 'data-om-view-heading';

/** Marks the shell's drawers (`ResponsiveDrawers`): the focus waits until they have closed. */
export const DRAWER_ATTR = 'data-om-drawer';

/** The longest wait for the heading (a room's header renders once its details arrived). */
const MAX_WAIT_MS = 3000;

/** Frames without an open drawer before acting (a closing drawer stays in the page during its exit motion). */
const CALM_FRAMES = 2;

/**
 * `restore` gives Fluent's own focus restore (to the control that opened the panel or drawer) this long after the
 * drawers are gone: Tabster restores from a timer once it noticed the removal.
 */
const RESTORE_GRACE_MS = 150;

/**
 * `move`: focus the heading unless something inside the main area took the focus meanwhile (the highlighted
 * message of a message route). `restore`: only when the focus fell to the page.
 */
export type ViewFocusMode = 'move' | 'restore';

/** The view's heading: the main area's `h1`, else the page's (the sign-in card). */
function viewHeading(doc: Document): HTMLElement | null {
  return doc.querySelector<HTMLElement>('main h1') ?? doc.querySelector<HTMLElement>('h1');
}

/** A modal dialog other than the shell's drawers is open: it owns the focus. */
function dialogOpen(doc: Document): boolean {
  return doc.querySelector(`[role="dialog"][aria-modal="true"]:not([${DRAWER_ATTR}]), [role="alertdialog"]`) !== null;
}

function focusLost(doc: Document): boolean {
  const active = doc.activeElement;
  return active === null || active === doc.body;
}

function focusHeading(heading: HTMLElement): void {
  if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
  heading.setAttribute(VIEW_HEADING_ATTR, '');
  heading.focus();
}

/**
 * Wait (animation frames, at most `MAX_WAIT_MS`) until no drawer is open or closing and the view's heading
 * exists, then focus it as `mode` says. Gives up when a dialog opens. `onMissing` runs when the wait ends with the
 * focus still lost (no heading to focus). Returns the cancel function.
 */
export function focusViewHeading(win: Window, mode: ViewFocusMode, onMissing: () => void): () => void {
  const doc = win.document;
  const start = doc.activeElement;
  const now = () => win.performance.now();
  const deadline = now() + MAX_WAIT_MS;
  let calm = 0;
  let calmSince = 0;
  let frame = 0;
  const tick = (): void => {
    if (dialogOpen(doc)) return;
    const active = doc.activeElement;
    const main = doc.querySelector('main');
    if (mode === 'move' && active !== null && active !== start && active !== doc.body && main?.contains(active)) return;
    calm = doc.querySelector(`[${DRAWER_ATTR}]`) === null ? calm + 1 : 0;
    if (calm === 1) calmSince = now();
    const settled = calm >= CALM_FRAMES && (mode === 'move' || now() - calmSince >= RESTORE_GRACE_MS);
    if (settled) {
      if (mode === 'restore' && !focusLost(doc)) return;
      const heading = viewHeading(doc);
      if (heading) {
        focusHeading(heading);
        return;
      }
    }
    if (now() > deadline) {
      if (focusLost(doc)) onMissing();
      return;
    }
    frame = win.requestAnimationFrame(tick);
  };
  frame = win.requestAnimationFrame(tick);
  return () => win.cancelAnimationFrame(frame);
}
